// "Run the audit, then fill the gaps" — borrowed from the Guru Cloud AI PM
// Gantt-integrity audit. A deterministic pass names exactly what each active
// initiative is missing (date / owner / blocker / team, dates that cannot
// hold, no movement), and the SAME list is handed to Merlin's next run as its
// GAPS TO CLOSE block, so the page shows what Merlin will ask about next.

import type { IntegrityReport } from '../../services/roadmapReports';
import { GAP_KIND_LABELS } from './roadmapViewUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  report: IntegrityReport | null;
  onSelect: (key: string) => void;
}

export function IntegrityPanel({ report, onSelect }: Props) {
  if (!report || report.active_count === 0) return null;
  const kinds = Object.keys(report.summary).filter((k) => report.summary[k] > 0);
  return (
    <section className={`${styles.panel} ${styles.gapPanel}`} aria-label="Roadmap integrity audit">
      <div className={styles.panelHead}>
        <h2>Gaps Merlin will ask about next run</h2>
        <span className={styles.meta}>
          {report.clean_count} of {report.active_count} open initiatives are complete · checked {report.as_of}
        </span>
      </div>
      {report.gaps.length === 0 ? (
        <div className={styles.clean}>No gaps — every active initiative has a date (or an open ask), an owner, a team and live blockers.</div>
      ) : (
        <>
          <div className={styles.gapSummary}>
            {kinds.map((k) => (
              <span key={k} className={styles.gapCount} title={GAP_KIND_LABELS[k] ?? k}>
                <strong>{report.summary[k]}</strong> {GAP_KIND_LABELS[k] ?? k}
              </span>
            ))}
          </div>
          {kinds.map((k) => (
            <div key={k} className={styles.gapGroup}>
              {/* One heading says what is missing; the list is only the names
                  (it used to repeat the same sentence on every line). */}
              <h3>{GAP_KIND_LABELS[k] ?? k}</h3>
              <ul className={styles.gapList}>
                {report.gaps.filter((g) => g.kind === k).map((g, idx) => (
                  <li key={`${g.initiative_key}:${k}:${idx}`} title={g.detail}>
                    <button type="button" className={styles.linkBtn} onClick={() => onSelect(g.initiative_key)}>
                      {g.title}
                    </button>{' '}
                    <span className={styles.gapWho}>({g.people.length ? g.people.join(', ') : 'nobody named'})</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
